import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Plus, RefreshCw, ExternalLink, AlertTriangle, Loader2, Trash2, ArrowRight, Paperclip, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { expirationColor } from '@/lib/permitDate';
import { pillStyle } from '@/components/StatusDot';
import api from '@/lib/api';
import { useConfirm } from '@/components/ConfirmDialog';
import { LinkExistingDialog } from '@/components/LinkExistingDialog';
import { inProject, buildProjectLinkPatch } from '@/lib/projectLink';

const JOB_TYPES = ['New Building', 'Alteration', 'Demolition', 'Other'];
const FILING_TYPES = ['NB', 'Alt-1', 'Alt-2', 'Alt-3', 'Plumbing', 'Electrical', 'Sprinkler', 'Fire Alarm', 'Mechanical', 'Other'];
const FILING_TYPE_COLORS = {
  'NB': '#010A21', 'Alt-1': '#1B4A76', 'Alt-2': '#1B4A76', 'Alt-3': '#1B4A76',
  'Plumbing': '#D97706', 'Electrical': '#D97706', 'Sprinkler': '#16A34A',
  'Fire Alarm': '#E5441A', 'Mechanical': '#D97706', 'Other': '#031333',
};

function FilingTypeDot({ type }) {
  return (
    <span className="inline-flex items-center" style={pillStyle(FILING_TYPE_COLORS[type])}>{type || 'Other'}</span>
  );
}

const fmtDate = (iso) => {
  if (!iso) return '\u00A0';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '\u00A0' : d.toLocaleDateString();
};

// BIS (traditional all-digit jobs) vs DOB NOW (letters/dashes) link label.
const systemLabel = (f) => f?.filing_system || (/^\d+$/.test(String(f?.job_number || '').trim()) ? 'BIS' : 'DOB NOW');

const EMPTY_ADD = { job_number: '', job_type: 'New Building', filing_type: 'NB', description: '', property_id: '', project_id: '', notes: '' };

// scope: 'project' (default, ProjectDetail) | 'property' (Property Profile) | 'all' (Properties hub)
export function DobFilings({ projectId, propertyId, scope = 'project', onGoToProjects, onUpdated }) {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [filings, setFilings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [projects, setProjects] = useState([]);

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_ADD);
  const [fetchAfterAdd, setFetchAfterAdd] = useState(true);
  const [saving, setSaving] = useState(false);

  const [detail, setDetail] = useState(null);
  const [refreshingId, setRefreshingId] = useState(null);
  const [detailWarning, setDetailWarning] = useState(null);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState(null);
  const [docCounts, setDocCounts] = useState({});

  // Link Existing (project scope only)
  const [linkOpen, setLinkOpen] = useState(false);
  const [linking, setLinking] = useState(false);
  const [linkCandidates, setLinkCandidates] = useState([]);
  const showLink = scope === 'project' && !!projectId && !!propertyId;

  const showPropertyCol = scope === 'all';

  const fetchFilings = useCallback(() => {
    let url = '/dob-filings';
    if (scope === 'project') url += `?project_id=${projectId}`;
    else if (scope === 'property') url += `?property_id=${propertyId}`;
    else url += '?all=true';
    return api.get(url)
      .then(r => setFilings(Array.isArray(r.data) ? r.data : []))
      .catch(() => setFilings([]));
  }, [scope, projectId, propertyId]);

  useEffect(() => {
    setLoading(true);
    fetchFilings().finally(() => setLoading(false));
    api.get('/properties').then(r => setProperties(Array.isArray(r.data) ? r.data : [])).catch(() => setProperties([]));
    api.get('/documents/link-counts', { params: { record_type: 'DOB Filing' } }).then(r => setDocCounts(r.data || {})).catch(() => setDocCounts({}));
    if (scope !== 'project') {
      api.get('/projects').then(r => setProjects(Array.isArray(r.data) ? r.data : [])).catch(() => setProjects([]));
    }
  }, [fetchFilings, scope]);

  const propertyOptions = (Array.isArray(properties) ? properties : []).map(p => ({
    value: p.id, label: p.display_name || p.address || p.id,
  }));

  const formPropId = scope === 'property' ? propertyId : addForm.property_id;
  const linkedProjectOptions = (Array.isArray(projects) ? projects : [])
    .filter(p => formPropId && p.property_id === formPropId)
    .map(p => ({ value: p.id, label: `${p.project_number}: ${p.name}` }));

  const propName = (pid) => {
    const p = (Array.isArray(properties) ? properties : []).find(x => x.id === pid);
    return p ? (p.display_name || p.address || 'View property') : 'View property';
  };

  const openAdd = () => {
    setAddForm({
      ...EMPTY_ADD,
      property_id: scope === 'property' ? propertyId : (propertyId || ''),
      project_id: scope === 'project' ? (projectId || '') : '',
    });
    setFetchAfterAdd(true);
    setAddOpen(true);
  };

  // Fetch this property's DOB filings not already on this project, then open the picker.
  const openLink = async () => {
    setLinkOpen(true);
    try {
      const r = await api.get(`/dob-filings?property_id=${propertyId}`);
      const all = Array.isArray(r.data) ? r.data : [];
      setLinkCandidates(all.filter(f => !inProject(f, projectId)));
    } catch { setLinkCandidates([]); }
  };

  const linkSelected = async (ids) => {
    setLinking(true);
    try {
      await Promise.all(ids.map(id => {
        const f = linkCandidates.find(x => x.id === id);
        return api.put(`/dob-filings/${id}`, buildProjectLinkPatch(f, projectId));
      }));
      toast.success(`Linked ${ids.length} filing${ids.length === 1 ? '' : 's'} to this project`);
      setLinkOpen(false);
      fetchFilings();
      if (onUpdated) onUpdated();
    } catch (e) { toast.error('Could not link some filings'); }
    setLinking(false);
  };

  const createFiling = async () => {
    if (!addForm.job_number.trim()) return toast.error('Job / Filing Number is required');
    setSaving(true);
    try {
      const r = await api.post('/dob-filings', {
        project_id: (scope === 'project' ? projectId : addForm.project_id) || null,
        property_id: (scope === 'property' ? propertyId : addForm.property_id) || null,
        job_number: addForm.job_number.trim(),
        job_type: addForm.job_type,
        filing_type: addForm.filing_type,
        description: addForm.description,
        notes: addForm.notes,
      });
      let created = r.data;
      if (fetchAfterAdd) {
        try {
          const fr = await api.post(`/dob-filings/${created.id}/fetch`);
          created = fr.data;
          if (fr.data?.warning) toast.warning(fr.data.warning);
          else toast.success('Filing added: live data fetched');
        } catch { toast.success('Filing added (live fetch failed)'); }
      } else {
        toast.success('Filing added');
      }
      setAddOpen(false);
      setAddForm(EMPTY_ADD);
      fetchFilings();
      if (onUpdated) onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error adding filing');
    }
    setSaving(false);
  };

  const refreshFiling = async (id, isDetail = false) => {
    setRefreshingId(id);
    try {
      const r = await api.post(`/dob-filings/${id}/fetch`);
      setFilings(prev => prev.map(f => (f.id === id ? r.data : f)));
      if (isDetail) {
        setDetail(r.data);
        setDetailWarning(r.data?.warning || null);
      }
      if (r.data?.warning) toast.warning(r.data.warning);
      else toast.success('Refreshed from NYC Open Data');
      if (onUpdated) onUpdated();
    } catch { toast.error('Could not refresh filing'); }
    setRefreshingId(null);
  };

  const openDetail = (f) => { setDetail(f); setDetailWarning(f?.warning || null); };

  const refreshAllFilings = async (list) => {
    const items = Array.isArray(list) ? list : [];
    if (items.length === 0) return;
    setRefreshingAll(true);
    let done = 0;
    for (const f of items) {
      setRefreshProgress({ current: done + 1, total: items.length });
      try {
        const r = await api.post(`/dob-filings/${f.id}/fetch`);
        setFilings(prev => prev.map(x => (x.id === f.id ? r.data : x)));
      } catch { /* keep going */ }
      done++;
    }
    setRefreshingAll(false);
    setRefreshProgress(null);
    if (onUpdated) onUpdated();
    toast.success(`Refreshed ${done} filing${done === 1 ? '' : 's'}`);
  };

  const saveDetail = async (patch) => {
    if (!detail) return;
    setDetail(prev => ({ ...prev, ...patch }));
    try {
      const r = await api.put(`/dob-filings/${detail.id}`, patch);
      setDetail(r.data);
      setFilings(prev => prev.map(f => (f.id === r.data.id ? r.data : f)));
    } catch { toast.error('Could not save'); fetchFilings(); }
  };

  const deleteFiling = async (id) => {
    const ok = await confirm({ title: 'Delete DOB filing', description: 'Delete this DOB filing? This cannot be undone.', confirmLabel: 'Delete', destructive: true });
    if (!ok) return;
    try {
      await api.delete(`/dob-filings/${id}`);
      toast.success('Filing deleted');
      setDetail(null);
      fetchFilings();
      if (onUpdated) onUpdated();
    } catch { toast.error('Could not delete filing'); }
  };

  const rows = Array.isArray(filings) ? filings : [];
  const lastSynced = (() => {
    const dates = rows.map(f => f.last_fetched_at).filter(Boolean).sort();
    return dates.length ? dates[dates.length - 1] : null;
  })();

  const emptyText = scope === 'project'
    ? 'No DOB filings tracked for this project.'
    : scope === 'property'
      ? 'No DOB filings tracked for this property.'
      : 'No DOB filings tracked yet.';

  const noLinkedProjects = scope === 'property' && (Array.isArray(projects) ? projects : []).filter(p => p.property_id === propertyId).length === 0;
  const colCount = showPropertyCol ? 6 : 5;

  if (loading) return <p className="text-sm text-muted-foreground py-4">Loading DOB filings...</p>;

  return (
    <div className="space-y-3" data-testid="dob-filings">
      {rows.length === 0 ? (
        <div className="py-4 space-y-3" data-testid="dob-empty">
          <p className="text-sm text-muted-foreground">{emptyText}</p>
          <div className="flex items-center gap-2">
            <Button className="bg-primary hover:bg-primary/90" onClick={openAdd} data-testid="add-dob-filing-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Filing
            </Button>
            {showLink && (
              <Button variant="outline" className="border-border" onClick={openLink} data-testid="link-existing-dob-btn-empty">
                <Link2 className="h-4 w-4 mr-1" /> Link Existing
              </Button>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground" data-testid="dob-last-synced">
                {lastSynced ? `Last synced: ${fmtDate(lastSynced)}` : 'Never synced'}
              </span>
              <Button size="sm" variant="outline" className="border-border" disabled={refreshingAll || rows.length === 0} onClick={() => refreshAllFilings(rows)} data-testid="refresh-all-filings-btn">
                {refreshingAll ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
                {refreshingAll && refreshProgress ? `Refreshing ${refreshProgress.current} of ${refreshProgress.total}...` : 'Refresh All'}
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" className="bg-primary hover:bg-primary/90" onClick={openAdd} data-testid="add-dob-filing-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Filing
              </Button>
              {showLink && (
                <Button size="sm" variant="outline" className="border-border" onClick={openLink} data-testid="link-existing-dob-btn">
                  <Link2 className="h-4 w-4 mr-1" /> Link Existing
                </Button>
              )}
            </div>
          </div>
          <div className="border border-border rounded-md bg-card overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-xs text-muted-foreground">Job #</TableHead>
                  {showPropertyCol && <TableHead className="text-xs text-muted-foreground">Property</TableHead>}
                  <TableHead className="text-xs text-muted-foreground">Filing Type</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Job Status</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Expiration</TableHead>
                  <TableHead className="text-xs text-muted-foreground text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(f => (
                  <TableRow key={f.id} className="border-border hover:bg-secondary/20" data-testid={`dob-row-${f.id}`}>
                    <TableCell style={{ fontFamily: "'DM Mono', monospace", fontSize: '14px' }}>
                      {f.dob_now_url ? (
                        <a href={f.dob_now_url} target="_blank" rel="noreferrer" className="text-[#1B4A76] hover:underline" data-testid={`dob-joblink-${f.id}`}>{f.job_number}</a>
                      ) : (
                        <span style={{ color: '#010A21' }}>{f.job_number}</span>
                      )}
                      {docCounts[f.id] > 0 && (
                        <button onClick={() => navigate(`/documents?record_type=${encodeURIComponent('DOB Filing')}&record_id=${f.id}`)} className="mt-0.5 flex items-center gap-1 text-xs text-[#1B4A76] hover:underline" style={{ fontFamily: "'DM Sans', sans-serif" }} data-testid={`dob-doc-count-${f.id}`}>
                          <Paperclip className="h-3 w-3" /> {docCounts[f.id]} document{docCounts[f.id] === 1 ? '' : 's'}
                        </button>
                      )}
                    </TableCell>
                    {showPropertyCol && (
                      <TableCell className="text-sm">
                        {f.property_id
                          ? <button onClick={() => navigate(`/properties/${f.property_id}`)} className="text-[#1B4A76] hover:underline text-left" data-testid={`dob-property-link-${f.id}`}>{f.property_display_name || f.property_address || 'View'}</button>
                          : <span className="text-muted-foreground">-</span>}
                      </TableCell>
                    )}
                    <TableCell><FilingTypeDot type={f.filing_type} /></TableCell>
                    <TableCell style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '12px' }}>{f.job_status || '\u00A0'}</TableCell>
                    <TableCell style={{ fontFamily: "'DM Mono', monospace", fontSize: '14px', color: expirationColor(f.expiration_date) }}>{f.expiration_date || '\u00A0'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-primary" disabled={refreshingId === f.id} onClick={() => refreshFiling(f.id)} data-testid={`dob-refresh-${f.id}`}>
                          {refreshingId === f.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                          <span className="ml-1">Refresh</span>
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 px-2 border-border" onClick={() => openDetail(f)} data-testid={`dob-view-${f.id}`}>View</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {/* Add Filing slide-out */}
      <Sheet open={addOpen} onOpenChange={setAddOpen}>
        <SheetContent className="bg-card border-border w-full sm:max-w-md overflow-y-auto" data-testid="dob-add-sheet">
          <SheetHeader>
            <SheetTitle style={{ color: '#010A21' }}>Add DOB Filing</SheetTitle>
            <SheetDescription>Track a DOB job number with live NYC Open Data status.</SheetDescription>
          </SheetHeader>
          <div className="space-y-4 mt-5">
            <div>
              <Label className="text-xs text-muted-foreground">Job / Filing Number *</Label>
              <Input value={addForm.job_number} onChange={e => setAddForm({ ...addForm, job_number: e.target.value })} placeholder="BIS Job # (e.g. 123456789) or DOB NOW Filing # (e.g. M00123456-I1)" className="bg-background border-border mt-1" style={{ fontFamily: "'DM Mono', monospace" }} data-testid="dob-add-job-number" />
              <p className="text-[11px] text-muted-foreground mt-1">Enter either a numeric BIS Job Number or an alphanumeric DOB NOW Filing Number. Data is fetched from the matching source automatically.</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Job Type</Label>
              <Select value={addForm.job_type} onValueChange={v => setAddForm({ ...addForm, job_type: v })}>
                <SelectTrigger className="bg-background border-border mt-1" data-testid="dob-add-job-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">{JOB_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Filing Type</Label>
              <Select value={addForm.filing_type} onValueChange={v => setAddForm({ ...addForm, filing_type: v })}>
                <SelectTrigger className="bg-background border-border mt-1" data-testid="dob-add-filing-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">{FILING_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Description</Label>
              <Input value={addForm.description} onChange={e => setAddForm({ ...addForm, description: e.target.value })} placeholder="Optional" className="bg-background border-border mt-1" data-testid="dob-add-description" />
            </div>

            {scope === 'property' ? (
              <div>
                <Label className="text-xs text-muted-foreground">Property</Label>
                <Input value={propName(propertyId)} disabled className="bg-secondary/40 border-border mt-1" data-testid="dob-add-property-locked" />
              </div>
            ) : (
              <div>
                <Label className="text-xs text-muted-foreground">Property {scope === 'all' && <span className="text-muted-foreground/60">(recommended)</span>}</Label>
                <div className="mt-1">
                  <SearchableSelect options={propertyOptions} value={addForm.property_id} onChange={v => setAddForm({ ...addForm, property_id: v, project_id: '' })} placeholder="Link a property (optional)" searchPlaceholder="Search properties..." emptyText="No properties." testid="dob-add-property" />
                </div>
              </div>
            )}

            {scope !== 'project' && (
              noLinkedProjects && scope === 'property' ? (
                <div className="rounded-md border border-border bg-secondary/30 px-3 py-2.5" data-testid="dob-add-no-projects">
                  <p className="text-xs text-muted-foreground">Link a project to this property before adding DOB filings (optional). You can still add this filing without a project.</p>
                  {onGoToProjects && (
                    <button onClick={() => { setAddOpen(false); onGoToProjects(); }} className="text-xs text-[#1B4A76] hover:underline inline-flex items-center gap-1 mt-1" data-testid="dob-add-view-projects">
                      View Projects <ArrowRight className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ) : (
                <div>
                  <Label className="text-xs text-muted-foreground">Project <span className="text-muted-foreground/60">(optional)</span></Label>
                  <div className="mt-1">
                    <SearchableSelect options={linkedProjectOptions} value={addForm.project_id} onChange={v => setAddForm({ ...addForm, project_id: v })} placeholder={formPropId ? 'Link a project (optional)' : 'Select a property first'} searchPlaceholder="Search projects..." emptyText={formPropId ? 'No projects linked to this property.' : 'Select a property first.'} testid="dob-add-project" />
                  </div>
                </div>
              )
            )}

            <div>
              <Label className="text-xs text-muted-foreground">Notes</Label>
              <Textarea value={addForm.notes} onChange={e => setAddForm({ ...addForm, notes: e.target.value })} placeholder="Optional" className="bg-background border-border mt-1" rows={3} data-testid="dob-add-notes" />
            </div>
            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
              <div>
                <p className="text-sm font-medium" style={{ color: '#010A21' }}>Fetch live data after adding</p>
                <p className="text-xs text-muted-foreground">Pull status from NYC Open Data automatically</p>
              </div>
              <Switch checked={fetchAfterAdd} onCheckedChange={setFetchAfterAdd} data-testid="dob-add-fetch-toggle" />
            </div>
            <Button className="w-full bg-primary hover:bg-primary/90" disabled={saving} onClick={createFiling} data-testid="dob-add-submit">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {fetchAfterAdd ? 'Adding & fetching...' : 'Adding...'}</> : 'Add Filing'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Detail slide-out */}
      <Sheet open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <SheetContent className="bg-card border-border w-full sm:max-w-lg overflow-y-auto" data-testid="dob-detail-sheet">
          {detail && (
            <>
              <SheetHeader>
                <div className="flex items-center gap-3 flex-wrap">
                  <SheetTitle style={{ fontFamily: "'DM Mono', monospace", color: '#010A21' }} data-testid="dob-detail-title">{detail.job_number}</SheetTitle>
                  <FilingTypeDot type={detail.filing_type} />
                </div>
                <SheetDescription>DOB filing details and live NYC Open Data status.</SheetDescription>
                {detail.dob_now_url && (
                  <a href={detail.dob_now_url} target="_blank" rel="noreferrer" className="text-sm text-[#1B4A76] hover:underline inline-flex items-center gap-1 w-fit" data-testid="dob-detail-dobnow-link">
                    View on {systemLabel(detail)} <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </SheetHeader>

              <div className="flex items-center gap-2 mt-4">
                <Button size="sm" variant="outline" className="border-border" disabled={refreshingId === detail.id} onClick={() => refreshFiling(detail.id, true)} data-testid="dob-detail-refresh">
                  {refreshingId === detail.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
                  Refresh from Open Data
                </Button>
                <Button size="sm" variant="outline" className="border-border text-destructive hover:text-destructive" onClick={() => deleteFiling(detail.id)} data-testid="dob-detail-delete">
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                </Button>
              </div>

              {/* Linked Property / Project */}
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-4" data-testid="dob-detail-links">
                <p className="text-sm">
                  <span style={{ color: '#031333' }}>Property: </span>
                  {detail.property_id
                    ? <button onClick={() => navigate(`/properties/${detail.property_id}`)} className="text-[#1B4A76] hover:underline" data-testid="dob-detail-property-link">{detail.property_display_name || propName(detail.property_id)}</button>
                    : <span style={{ color: '#010A21' }}>-</span>}
                </p>
                <p className="text-sm">
                  <span style={{ color: '#031333' }}>Project: </span>
                  {(detail.project_id || (scope === 'project' && projectId))
                    ? <button onClick={() => navigate(`/projects/${detail.project_id || projectId}`)} className="text-[#1B4A76] hover:underline" data-testid="dob-detail-project-link">View project</button>
                    : <span style={{ color: '#010A21' }}>-</span>}
                </p>
                <p className="text-sm">
                  <span style={{ color: '#031333' }}>Documents: </span>
                  {docCounts[detail.id] > 0
                    ? <button onClick={() => navigate(`/documents?record_type=${encodeURIComponent('DOB Filing')}&record_id=${detail.id}`)} className="text-[#1B4A76] hover:underline inline-flex items-center gap-1" data-testid="dob-detail-doc-count"><Paperclip className="h-3 w-3" /> {docCounts[detail.id]} linked</button>
                    : <span style={{ color: '#010A21' }}>None</span>}
                </p>
              </div>

              {/* Editable details */}
              <div className="space-y-4 mt-5">
                <p className="text-xs font-semibold" style={{ color: '#031333' }}>Details</p>
                <div>
                  <Label className="text-xs text-muted-foreground">Job / Filing Number</Label>
                  <Input value={detail.job_number || ''} onChange={e => setDetail({ ...detail, job_number: e.target.value })} onBlur={e => saveDetail({ job_number: e.target.value })} className="bg-background border-border mt-1" style={{ fontFamily: "'DM Mono', monospace" }} data-testid="dob-detail-job-number" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Job Type</Label>
                    <Select value={detail.job_type || 'Other'} onValueChange={v => saveDetail({ job_type: v })}>
                      <SelectTrigger className="bg-background border-border mt-1" data-testid="dob-detail-job-type"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-card border-border">{JOB_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Filing Type</Label>
                    <Select value={detail.filing_type || 'Other'} onValueChange={v => saveDetail({ filing_type: v })}>
                      <SelectTrigger className="bg-background border-border mt-1" data-testid="dob-detail-filing-type"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-card border-border">{FILING_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Description</Label>
                  <Input value={detail.description || ''} onChange={e => setDetail({ ...detail, description: e.target.value })} onBlur={e => saveDetail({ description: e.target.value })} className="bg-background border-border mt-1" data-testid="dob-detail-description" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Notes</Label>
                  <Textarea value={detail.notes || ''} onChange={e => setDetail({ ...detail, notes: e.target.value })} onBlur={e => saveDetail({ notes: e.target.value })} className="bg-background border-border mt-1" rows={3} data-testid="dob-detail-notes" />
                </div>
              </div>

              {/* Open Data read-only */}
              <div className="space-y-3 mt-6 pt-4 border-t border-border">
                <p className="text-xs font-semibold" style={{ color: '#031333' }}>NYC Open Data</p>
                {detail.last_fetched_at ? (
                  <>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3" data-testid="dob-detail-opendata">
                      {[
                        ['Job Status', detail.job_status],
                        ['Filing Status', detail.filing_status],
                        ['Applicant', detail.applicant_name],
                        ['Approved Date', detail.approved_date],
                        ['Issued Date', detail.issued_date],
                        ['Expiration Date', detail.expiration_date],
                      ].map(([label, val]) => (
                        <div key={label}>
                          <p className="text-xs " style={{ color: '#031333', fontFamily: "'DM Sans', sans-serif" }}>{label}</p>
                          <p style={{ fontFamily: "'DM Mono', monospace", fontSize: '14px', color: label === 'Expiration Date' ? (expirationColor(val) || '#010A21') : '#010A21' }}>{val || '\u00A0'}</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">Last fetched: {fmtDate(detail.last_fetched_at)}</p>
                  </>
                ) : (
                  <div className="space-y-2" data-testid="dob-detail-nolive">
                    <p className="text-sm text-muted-foreground">No live data yet. Click Refresh to fetch from NYC Open Data.</p>
                    <Button size="sm" variant="outline" className="border-border" disabled={refreshingId === detail.id} onClick={() => refreshFiling(detail.id, true)} data-testid="dob-detail-refresh-empty">
                      {refreshingId === detail.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
                      Refresh
                    </Button>
                  </div>
                )}
                {detailWarning && (
                  <div className="flex items-start gap-2 rounded-md px-3 py-2 mt-2" style={{ backgroundColor: '#FEF3C7', color: '#B45309' }} data-testid="dob-detail-warning">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <p className="text-xs">{detailWarning}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <LinkExistingDialog
        open={linkOpen}
        onOpenChange={setLinkOpen}
        title="Link Existing DOB Filings"
        description="Select DOB filings recorded on this property to also track under this project."
        records={linkCandidates}
        renderPrimary={(f) => f.job_number}
        renderSecondary={(f) => `${f.filing_type || ''}${f.job_status ? ` · ${f.job_status}` : ''}`}
        linking={linking}
        onConfirm={linkSelected}
        emptyText="No unlinked DOB filings found for this property."
        testid="link-existing-dob"
      />
    </div>
  );
}

export default DobFilings;
